import { useState } from 'react'
import { useOnboarding } from '../../../contexts/OnboardingContext'
import { Mail, Phone, Plus, X, Check, Send } from 'lucide-react'

export function InvitationsStep() {
  const { data, updateData, resetOnboarding } = useOnboarding()
  const [newInvitation, setNewInvitation] = useState({ contact: '', type: 'email' as 'email' | 'phone', countryCode: '+1' })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [sentInvitations, setSentInvitations] = useState<string[]>([])
  const [showCountryDropdown, setShowCountryDropdown] = useState(false)
  
  const validateEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
  }
  
  const validatePhone = (phone: string) => {
    // Remove all non-digit characters except + at the beginning
    const cleaned = phone.replace(/[^\d+]/g, '')
    // Check if it starts with + and has 10-15 digits
    return /^\+\d{10,15}$/.test(cleaned)
  }
  
  
  // Common country codes
  const countryCodes = [
    { code: '+1', country: 'US/CA', flag: '🇺🇸' },
    { code: '+44', country: 'UK', flag: '🇬🇧' },
    { code: '+61', country: 'AU', flag: '🇦🇺' },
    { code: '+91', country: 'IN', flag: '🇮🇳' },
    { code: '+86', country: 'CN', flag: '🇨🇳' },
    { code: '+81', country: 'JP', flag: '🇯🇵' },
    { code: '+49', country: 'DE', flag: '🇩🇪' },
    { code: '+33', country: 'FR', flag: '🇫🇷' },
    { code: '+39', country: 'IT', flag: '🇮🇹' },
    { code: '+34', country: 'ES', flag: '🇪🇸' },
    { code: '+55', country: 'BR', flag: '🇧🇷' },
    { code: '+52', country: 'MX', flag: '🇲🇽' },
    { code: '+27', country: 'ZA', flag: '🇿🇦' },
    { code: '+234', country: 'NG', flag: '🇳🇬' },
    { code: '+254', country: 'KE', flag: '🇰🇪' },
  ]
  
  const addInvitation = () => {
    const { contact, type } = newInvitation
    
    if (!contact.trim()) return
    
    if (type === 'email' && !validateEmail(contact)) {
      alert('Please enter a valid email address')
      return
    }
    
    if (type === 'phone') {
      const fullPhone = contact.startsWith('+') ? contact : newInvitation.countryCode + contact
      if (!validatePhone(fullPhone)) {
        alert('Please enter a valid phone number with 10-15 digits')
        return
      }
    }
    
    // Check for duplicates
    const exists = data.invitations.some(inv => inv.contact === contact)
    if (exists) {
      alert('This contact has already been added')
      return
    }
    
    const finalContact = type === 'phone' && !contact.startsWith('+') 
      ? newInvitation.countryCode + contact 
      : contact
    
    updateData({
      invitations: [...data.invitations, { contact: finalContact, type }]
    })
    
    setNewInvitation({ contact: '', type: 'email', countryCode: newInvitation.countryCode })
  }
  
  const removeInvitation = (index: number) => {
    const updated = data.invitations.filter((_, i) => i !== index)
    updateData({ invitations: updated })
  }
  
  const completeOnboarding = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/v1/auth/complete-onboarding`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      })
      
      if (!response.ok) {
        console.error('Failed to mark onboarding as complete')
      }
    } catch (error) {
      console.error('Error completing onboarding:', error)
    }
  }

  const sendInvitations = async () => {
    if (data.invitations.length === 0) {
      // Skip invitations and go to dashboard
      await completeOnboarding()
      resetOnboarding()
      // Force a full page reload to refresh auth state
      window.location.href = '/dashboard'
      return
    }
    
    setIsSubmitting(true)
    
    try {
      const results = await Promise.all(
        data.invitations.map(async (invitation) => {
          const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/v1/invitations`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
            },
            body: JSON.stringify({
              contact: invitation.contact,
              type: invitation.type,
              role: 'MEMBER'
            })
          })
          
          if (response.ok) {
            return invitation.contact
          }
          throw new Error(`Failed to invite ${invitation.contact}`)
        })
      )
      
      setSentInvitations(results)
      
      // Mark onboarding complete before redirect
      await completeOnboarding()
      
      // Auto-redirect after 3 seconds
      setTimeout(() => {
        resetOnboarding()
        // Force a full page reload to refresh auth state
        window.location.href = '/dashboard'
      }, 3000)
      
    } catch (error) {
      console.error('Failed to send invitations:', error)
      alert('Some invitations failed to send. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }
  
  const skipInvitations = async () => {
    await completeOnboarding()
    resetOnboarding()
    // Force a full page reload to refresh auth state
    window.location.href = '/dashboard'
  }
  
  if (sentInvitations.length > 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-8 text-center">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Check className="w-8 h-8 text-green-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Invitations sent!
        </h2>
        <p className="text-gray-600 mb-6">
          We've sent invitations to {sentInvitations.length} team member{sentInvitations.length > 1 ? 's' : ''}.
          They'll receive an email or SMS with instructions to join your team.
        </p>
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <h3 className="font-medium text-gray-900 mb-2">Invited:</h3>
          <ul className="text-sm text-gray-600 space-y-1">
            {sentInvitations.map(contact => (
              <li key={contact}>{contact}</li>
            ))}
          </ul>
        </div>
        <p className="text-sm text-gray-500">
          Redirecting to dashboard in a few seconds...
        </p>
      </div>
    )
  }
  
  return (
    <div className="bg-white rounded-lg shadow-sm p-8">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Invite your team
        </h2>
        <p className="text-gray-600">
          Add team members who will be using ClockInNOut to track their time
        </p>
      </div>
      
      <div className="space-y-6">
        {/* Add invitation form */}
        <div className="p-4 bg-gray-50 rounded-lg">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1">
              {newInvitation.type === 'phone' ? (
                <div className="flex gap-2">
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setShowCountryDropdown(!showCountryDropdown)}
                      className="px-3 py-2 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-transparent flex items-center gap-1"
                    >
                      <span>{countryCodes.find(c => c.code === newInvitation.countryCode)?.flag || '🌍'}</span>
                      <span className="font-medium">{newInvitation.countryCode}</span>
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {showCountryDropdown && (
                      <div className="absolute z-10 mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                        {countryCodes.map((country) => (
                          <button
                            key={country.code}
                            type="button"
                            onClick={() => {
                              setNewInvitation({ ...newInvitation, countryCode: country.code })
                              setShowCountryDropdown(false)
                            }}
                            className="w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center gap-2"
                          >
                            <span>{country.flag}</span>
                            <span className="font-medium">{country.code}</span>
                            <span className="text-gray-500 text-sm">{country.country}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <input
                    type="tel"
                    value={newInvitation.contact}
                    onChange={(e) => {
                      // Only allow digits, spaces, hyphens, and parentheses
                      const value = e.target.value.replace(/[^\d\s\-\(\)]/g, '')
                      setNewInvitation({ ...newInvitation, contact: value })
                    }}
                    placeholder="555 123 4567"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    onKeyPress={(e) => e.key === 'Enter' && addInvitation()}
                  />
                </div>
              ) : (
                <input
                  type="email"
                  value={newInvitation.contact}
                  onChange={(e) => setNewInvitation({ ...newInvitation, contact: e.target.value })}
                  placeholder="colleague@company.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  onKeyPress={(e) => e.key === 'Enter' && addInvitation()}
                />
              )}
            </div>
            <select
              value={newInvitation.type}
              onChange={(e) => {
                setNewInvitation({ ...newInvitation, type: e.target.value as 'email' | 'phone', contact: '' })
                setShowCountryDropdown(false)
              }}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="email">Email</option>
              <option value="phone">Phone</option>
            </select>
            <button
              type="button"
              onClick={addInvitation}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add
            </button>
          </div>
        </div>
        
        {/* Invitations list */}
        {data.invitations.length > 0 && (
          <div>
            <h3 className="font-medium text-gray-900 mb-3">
              Team members to invite ({data.invitations.length})
            </h3>
            <div className="space-y-2">
              {data.invitations.map((invitation, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg">
                  <div className="flex items-center">
                    {invitation.type === 'email' ? (
                      <Mail className="w-4 h-4 text-gray-400 mr-3" />
                    ) : (
                      <Phone className="w-4 h-4 text-gray-400 mr-3" />
                    )}
                    <span className="text-gray-900">{invitation.contact}</span>
                    <span className="ml-2 text-sm text-gray-500 capitalize">({invitation.type})</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeInvitation(index)}
                    className="text-gray-400 hover:text-red-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {data.invitations.length === 0 && (
          <div className="text-center py-8">
            <Mail className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">
              No team members added yet. Add email addresses or phone numbers above.
            </p>
          </div>
        )}
      </div>
      
      {/* Navigation */}
      <div className="mt-8 flex justify-between">
        <button
          type="button"
          onClick={skipInvitations}
          className="px-6 py-2 text-gray-600 hover:text-gray-900 font-medium"
        >
          Skip for now
        </button>
        <button
          type="button"
          onClick={sendInvitations}
          disabled={isSubmitting}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50 flex items-center"
        >
          {isSubmitting ? (
            <>
              <Send className="w-4 h-4 mr-2 animate-pulse" />
              Sending...
            </>
          ) : data.invitations.length > 0 ? (
            <>
              <Send className="w-4 h-4 mr-2" />
              Send Invitations
            </>
          ) : (
            'Go to Dashboard'
          )}
        </button>
      </div>
    </div>
  )
}

export default InvitationsStep